import {
  Text,
  HStack,
  Icon,
  Tag,
  Box,
  IconButton,
  StackDivider,
  Tooltip,
  SimpleGrid,
} from '@wegroup/design-system';
import { uniq } from 'lodash';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PartyType } from '../../../../../../../../../../types/SMT';
import LabeledParamFallback from '../../../../../../../../../common/src/components/label-param-fallback/LabelParamFallback';
import { Option } from '../../../../../../../../../design-system/src/content/components/SearchPickerInput';
import { toEuro } from '../../../../../../../../../sales-conversation/src/utils/stringUtils';
import { InsurancePolicy } from '../../../../../../../types/AdvisoryReport';
import useAdvisoryReportCurrency from '../../../../../hooks/useAdvisoryReportCurrency';
import InsurancePolicyTableRowActions from '../../InsurancePolicyTableRowActions';

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  insurancePolicy: InsurancePolicy;
  withActions?: boolean;
  customRiskObjects?: Option[];
  controlled?: boolean;
  partyType: PartyType;
  hideAIBanner?: boolean;
  customActions?: ReactNode;

  // Actions
  onEdit?: (insurancePolicy: InsurancePolicy) => void;
  onRemove?: (id: string, packageId?: string) => void;
}

const InsurancePolicyHeaderInfo: React.FC<Props> = ({
  isOpen,
  onToggle,
  insurancePolicy,
  controlled,
  customActions,
  withActions,
  customRiskObjects,
  onEdit,
  onRemove,
  partyType,
  hideAIBanner,
}) => {
  const { t } = useTranslation();
  const isPackage = !!insurancePolicy.policy_package_id;
  const amountOfRisks = uniq(
    insurancePolicy.coverages?.map(
      ({ risk_object_id, risk_object }) => risk_object_id || risk_object,
    ),
  ).length;

  return (
    <HStack spacing="2" justify="space-between" align="flex-start">
      <HStack
        spacing="2"
        flexGrow={1}
        flexWrap={isPackage ? 'wrap' : 'nowrap'}
        gap="2"
        justify="space-between"
      >
        <Title
          isDraggable={withActions && !controlled}
          name={insurancePolicy.name}
          amountOfRisks={amountOfRisks}
          isPackage={isPackage}
          isTerminated={insurancePolicy.type === 'TERMINATED'}
        />
        {isPackage && (
          <MinimalInfo
            paymentInterval={insurancePolicy.payment_interval}
            premium={insurancePolicy.premium}
            policyNumber={insurancePolicy.policy_nr}
          />
        )}
      </HStack>
      <HStack
        spacing="2"
        divider={<StackDivider borderLeft="1px" borderColor="gray.50" />}
        flexShrink="0"
      >
        <HStack spacing="2">
          <InsuredElsewhereTag
            type={insurancePolicy.type}
            closedAtDistribution={insurancePolicy.closed_at_distribution}
          />
          {insurancePolicy.type === 'TERMINATED' && (
            <Tag variant="error">{t('TERMINATED')}</Tag>
          )}
          {withActions && (
            <InsurancePolicyTableRowActions
              insurancePolicy={insurancePolicy}
              customRiskObjects={customRiskObjects}
              controlled={controlled}
              onEdit={onEdit}
              onRemove={onRemove}
              partyType={partyType}
              hideAIBanner={hideAIBanner}
            />
          )}
          {customActions}
        </HStack>
        <IconButton
          size="sm"
          variant="grayed"
          onClick={onToggle}
          aria-label="Open insurance policy"
          icon={
            <Box
              transform={isOpen ? 'rotate(180deg)' : undefined}
              transition="0.2s"
            >
              <Icon name="GeneralActionsChevronDown" color="gray.300" />
            </Box>
          }
        />
      </HStack>
    </HStack>
  );
};

const MinimalInfo: React.FC<{
  policyNumber?: string;
  premium?: number;
  paymentInterval?: string;
}> = ({ policyNumber, paymentInterval, premium }) => {
  const { t } = useTranslation();
  const { currency } = useAdvisoryReportCurrency();

  return (
    <SimpleGrid gap="3" columns={2} flexGrow={1} maxW="sm">
      <LabeledParamFallback
        label={t('ADVISORY-REPORTS_CURRENT-INSURANCE_OVERVIEW_POLICY-NR')}
      >
        {policyNumber}
      </LabeledParamFallback>

      <LabeledParamFallback
        label={t('ADVISORY-REPORTS_CURRENT-INSURANCE_OVERVIEW_PREMIUM')}
      >
        {premium && paymentInterval
          ? `${toEuro(premium, currency)} / ${t(paymentInterval)}`
          : premium
          ? toEuro(premium, currency)
          : undefined}
      </LabeledParamFallback>
    </SimpleGrid>
  );
};

const Title: React.FC<{
  isDraggable?: boolean;
  isPackage?: boolean;
  name: string;
  amountOfRisks?: number;
  isTerminated?: boolean;
}> = ({ isPackage, isDraggable, isTerminated, name, amountOfRisks }) => {
  const { t } = useTranslation();

  return (
    <HStack spacing="2" flexGrow={isPackage ? undefined : 1}>
      {isDraggable && (
        <Icon name="GeneralActionsDrag" color="gray.300" boxSize={5} />
      )}
      <Text
        textStyle="normalBodyMedium"
        textDecoration={isTerminated ? 'line-through' : undefined}
      >
        {name}
      </Text>
      <Tag variant="gray" flexShrink="0">
        {amountOfRisks || 0}{' '}
        {amountOfRisks === 1
          ? t('RISK').toLowerCase()
          : t('RISKS').toLowerCase()}
      </Tag>
    </HStack>
  );
};

export const InsuredElsewhereTag: React.FC<{
  type: 'ACTIVE' | 'INACTIVE' | 'POLICY_ON_HOLD' | 'TERMINATED' | 'SIMULATION';
  closedAtDistribution?: boolean;
}> = ({ type, closedAtDistribution }) => {
  const { t } = useTranslation();

  return (
    <>
      {type === 'ACTIVE' && closedAtDistribution != null && (
        <>
          {closedAtDistribution ? (
            <Tag variant="info">
              {t(
                'ADVISORY-REPORTS_CURRENT-INSURANCE_OVERVIEW_PERSONAL-MANAGEMENT',
              )}
            </Tag>
          ) : (
            <Tag variant="warning">
              {t(
                'ADVISORY-REPORTS_CURRENT-INSURANCE_OVERVIEW_INSURED-ELSEWHERE',
              )}
            </Tag>
          )}
        </>
      )}
      {type === 'POLICY_ON_HOLD' && (
        <Tooltip
          label={t(
            'ADVISORY-REPORTS_CURRENT-INSURANCE_OVERVIEW_POLICY-ON-HOLD_DESCRIPTION',
          )}
        >
          <Tag variant="info">
            {t('ADVISORY-REPORTS_CURRENT-INSURANCE_OVERVIEW_POLICY-ON-HOLD')}
          </Tag>
        </Tooltip>
      )}
    </>
  );
};

export default InsurancePolicyHeaderInfo;
